import { useTranslation } from 'react-i18next';
import {
  useSavedFilterViews,
  type SavedFilterViews,
} from '~/hooks/useSavedFilterViews';
import { preferenceSlot } from '~/hooks/usePreferenceWriteQueue';
import { savedViewMessages } from '~/utils/savedViewMessages';
import type { GetUserPreferencesQuery } from '~/graphql/graphql';
import { filterRowsToSaved } from '~/types/filters';
import {
  INVENTORY_FILTER_DATA_KEY,
  isInventoryFilterData,
  readInventoryFilterRows,
  rowsToFilterData,
  type InventoryFilterRow,
} from './buyerInventoryFilterStorage';

const INVENTORY_VIEW = 'INVENTORY' as const;

const INVENTORY_PERSPECTIVE = 'BUYING' as const;

type Preferences = GetUserPreferencesQuery['userPreferences'] | undefined;

export type InventorySavedViews = SavedFilterViews<InventoryFilterRow>;

function activeIdOf(preferences: Preferences): string | null {
  return preferences?.buyerActiveFilterId ?? null;
}

// The unsaved filter is stored as a bare row list, so it is wrapped to read like a saved one.
function anonymousOf(preferences: Preferences): unknown {
  return { [INVENTORY_FILTER_DATA_KEY]: preferences?.buyerAnonymousFilter };
}

export function useInventorySavedViews(): InventorySavedViews {
  const { t } = useTranslation('inventory');

  return useSavedFilterViews<InventoryFilterRow>({
    view: INVENTORY_VIEW,
    perspective: INVENTORY_PERSPECTIVE,
    slot: preferenceSlot('inventory', INVENTORY_PERSPECTIVE),
    isOwnFilterData: isInventoryFilterData,
    readRows: readInventoryFilterRows,
    rowsToFilterData,
    activeIdOf,
    anonymousOf,
    anonymousPreferences: (rows) => ({
      buyerAnonymousFilter: filterRowsToSaved(rows) as unknown as Record<
        string,
        unknown
      >,
    }),
    messages: savedViewMessages(t, 'inventory.filter'),
  });
}
